import { useEffect, useRef } from "react"
import { NavigateFunction } from "react-router-dom"
import { AppDispatch } from "../../redux/store"
import { pushToast } from "../../redux/slices/notifications"

interface MutationResult {
  isSuccess: boolean
  isError: boolean
  data?: { status?: string }
  reset: () => void
}

interface Options {
  label: string
  isCreate: boolean
  saveResult: MutationResult
  deleteResult: MutationResult
  navigate: NavigateFunction
  backUrl: string | (() => string)
  dispatch: AppDispatch
}

export const useConfigMutationFeedback = ({
  label,
  isCreate,
  saveResult,
  deleteResult,
  navigate,
  backUrl,
  dispatch
}: Options) => {
  const backUrlRef = useRef(backUrl)
  backUrlRef.current = backUrl
  const resolveBackUrl = () =>
    typeof backUrlRef.current === "function"
      ? backUrlRef.current()
      : backUrlRef.current
  const {
    isSuccess: isSaveSuccess,
    isError: isSaveError,
    data: saveData,
    reset: resetSave
  } = saveResult
  const {
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
    data: deleteData,
    reset: resetDelete
  } = deleteResult

  useEffect(() => {
    if (isSaveSuccess && saveData?.status !== "error") {
      dispatch(
        pushToast({
          msg: `Successfully ${isCreate ? "created" : "updated"} ${label}`,
          theme: "success",
          timeout: 3000
        })
      )
      resetSave()
      if (isCreate) navigate(resolveBackUrl())
    }
  }, [
    dispatch,
    isCreate,
    isSaveSuccess,
    label,
    navigate,
    resetSave,
    saveData?.status
  ])

  useEffect(() => {
    if (isSaveError || saveData?.status === "error") {
      dispatch(
        pushToast({
          msg: `Failed to ${isCreate ? "create" : "update"} ${label}`,
          theme: "danger",
          timeout: 3000
        })
      )
      resetSave()
    }
  }, [dispatch, isCreate, isSaveError, label, resetSave, saveData?.status])

  useEffect(() => {
    if (isDeleteSuccess && deleteData?.status === "success") {
      dispatch(
        pushToast({
          msg: `Successfully deleted ${label}`,
          theme: "success",
          timeout: 3000
        })
      )
      resetDelete()
      navigate(resolveBackUrl())
    }
  }, [
    deleteData?.status,
    dispatch,
    isDeleteSuccess,
    label,
    navigate,
    resetDelete
  ])

  useEffect(() => {
    if (isDeleteError || deleteData?.status === "error") {
      dispatch(
        pushToast({
          msg: `Failed to delete ${label}`,
          theme: "danger",
          timeout: 3000
        })
      )
      resetDelete()
    }
  }, [deleteData?.status, dispatch, isDeleteError, label, resetDelete])
}
